import { Observable, Observer, Subscriber } from './observable.js';

/**
 * Subject acts as both an Observable and Observer.
 * It is multicast, meaning it can have multiple subscribers and notify them all simultaneously.
 */
class Subject<T> extends Observable<T> implements Observer<T> {

    #subscribers = new Set<Subscriber<T>>();

    constructor(start: (subscriber: Subscriber<T>) => void) {
        super((subscriber: Subscriber<T>) => {
            this.#subscribers.add(subscriber);
            // provide teardown logic to remove the subscriber.
            subscriber.addTeardown(() => this.#subscribers.delete(subscriber));
            // run provided start logic.
            start(subscriber);
        });
    }

    /**
     * Emits a value to all subscribed observers.
     *
     * @param value The value to emit.
     */
    next(value: T): void {
        this.#subscribers.forEach(subscriber => subscriber.next(value));
    }

    /**
     * Sends an error notification to all subscribers, then clears subscribers list.
     * @param error The error to send.
     */
    error(error: any): void {
        this.#subscribers.forEach(subscriber => subscriber.error(error));
        this.#subscribers.clear();
    }

    /**
     * Sends a complete notification to all subscribers, then clears subscribers list.
     */
    complete(): void {
        this.#subscribers.forEach(subscriber => subscriber.complete());
        this.#subscribers.clear();
    }
}

/**
 * BehaviorSubject is a type of Subject that requires an initial value and replays it or the latest value to new subscribers.
 */
export class BehaviorSubject<T> extends Subject<T> {

    /**
     * Keeps the current value of the BehaviorSubject.
     */
    #value: T;

    /**
     * Creates a new BehaviorSubject instance with an initial value.
     *
     * @param value The initial value emitted to subscribers upon subscription.
     */
    constructor(initialValue: T) {
        super((subscriber: Subscriber<T>) => {
            // emit the current value to new subscribers immediately after subscription.
            subscriber.next(this.#value);
        });
        this.#value = initialValue;
    }

    /**
     * @inheritdoc
     */
    next(value: T): void {
        this.#value = value;
        super.next(value);
    }

    /**
     * Gets the current value of the BehaviorSubject.
     */
    get value(): T {
        return this.#value;
    }
}

/**
 * BehaviorPartialSubject is a specialized version of BehaviorSubject that allows emitting partial updates to the current value.
 *
 * @example
 * const subject = new BehaviorPartialSubject({ a: 1, b: 2 });
 * subject.subscribe(value => console.log(value)); // Logs: { a: 1, b: 2 }
 * subject.next({ a: 3 }); // Logs: { a: 3, b: 2 }
 * subject.next({ b: 4 }); // Logs: { a: 3, b: 4 }
 */
export class BehaviorPartialSubject<T extends object> extends BehaviorSubject<T> {

    /**
     * @inheritdoc
     */
    next(value: Partial<T>): void {
        super.next({
            ...this.value,
            ...value,
        })
    }
}

/**
 * ReplaySubject is a type of Subject that can replay a specified number of past values to new subscribers.
 */
export class ReplaySubject<T> extends Subject<T> {

    /**
     * The maximum number of values to store and replay to new subscribers.
     */
    #bufferSize: number;
    /**
     * The buffer of values to replay to new subscribers.
     */
    #buffer: T[] = [];

    /**
     * Initializes a new instance of the ReplaySubject class.
     *
     * @param bufferSize The maximum number of values to store and replay to new subscribers.
     */
    constructor(bufferSize: number = 1) {
        super((subscriber: Subscriber<T>) => {
            // replay the buffer to the new subscriber.
            this.#buffer.forEach(value => subscriber.next(value));
        });

        this.#bufferSize = bufferSize;
    }

    /**
     * @inheritdoc
     */
    next(value: T): void {
        // add the new value to the buffer
        this.#buffer.push(value);
        // ensure the buffer does not exceed the specified size
        if (this.#buffer.length > this.#bufferSize) {
            this.#buffer.shift(); // remove the oldest value
        }
        // emit the new value to all current subscribers
        super.next(value);
    }

    /**
     * @inheritdoc
     */
    complete(): void {
        super.complete();
        // clear the buffer after completion
        this.#buffer.length = 0;
    }

    /**
     * @inheritdoc
     */
    error(error: any): void {
        super.error(error);
        // clear the buffer after error
        this.#buffer.length = 0;
    }
}
